import React, { useState, useEffect } from 'react';
import { Link, RouteComponentProps, useHistory } from 'react-router-dom';
import * as H from 'history';
import { Formik, Field } from 'formik';
import _ from 'lodash';

import './Home.css';
import {
  Section,
  FormGroup,
  TextboxField,
  TradingBlockLink,
  useAuthentication,
  Svg,
  FooterTeaser,
  SiteLogo,
  SiteName,
  ExternalLink,
  Header,
  SubHeader,
  // useTracking,
} from '@tradingblock/components';
import { TryLogin, GetUrlParams, GENERIC_LOGIN_ERROR_MSG } from '@tradingblock/api';
import { getStaticPath, getSubdirectory, toHashParams } from '../utils';
import { ConfigType, VirtualTradingPaths, config as Config } from '../clientConfig';
import {
  LoginJwtValue,
  ResponseCode,
  TradingBlockEnvironments,
  TwoFactorAuthenticationStates,
  UIClientTypes,
  UnauthenticatedTwoFactorAuthenticationVerifiedMethodData,
  UserProfileLevel,
} from '@tradingblock/types';
import jwt from 'jsonwebtoken';
import TwoFactorAuthenticationWrapper from './TwoFactorAuthenticationWrapper';
import { LoginCardAuthFooter } from './LoginCardAuthFooter';
import { getPath } from '../api/constants';

interface LoginValues {
  username: string;
  password: string;
}
interface FormValues {
  signin: LoginValues;
}

const getEnvironment = (location: H.Location<any>) => {
  const params = GetUrlParams(location.search);
  const environment = params['environment'] || undefined;
  if (environment) {
    return environment;
  } else if (
    Config.environment === TradingBlockEnvironments.virtual ||
    VirtualTradingPaths.includes(location.pathname)
  ) {
    return 'virtual';
  } else {
    return 'dashboard';
  }
};

const getConfig = (env: any): ConfigType => {
  if (env === 'virtual') {
    return Config.virtual;
  }
  return Config.dashboard;
};

export const Login: React.FunctionComponent<RouteComponentProps> = ({ location }) => {
  const [{ isAuthenticating }, { setIsAuthenticating }] = useAuthentication();
  const history: H.History<{}> = useHistory();

  const params = GetUrlParams(location.search);
  const env = getEnvironment(location);
  const isVirtual = env === 'virtual';
  const config = getConfig(env);
  const apiRootUrl = config.apiRoot || undefined;

  const redirect = params['redirect'];
  const next = params['next'];
  const grp = Config.siteGrp;
  const clientType = params['type'];
  const siteParam = grp ? `?grp=${grp}` : '';
  const group: 'mb' | 'tb' = grp === 'mb' ? 'mb' : 'tb';

  const [authError, setAuthError] = useState<string>();
  const [pageLoadedWithJs, setPageLoadedWithJs] = useState<boolean>();
  const [username, setUsername] = useState<string | undefined>();
  const [password, setPassword] = useState<string | null>(null);
  const [showTwoFactorAuthenticationComponent, setShowTwoFactorAuthenticationComponent] = useState<boolean>(false);
  const [verifiedTwoFactorAuthenticationMethodsData, setVerifiedTwoFactorAuthenticationMethodsData] = useState<UnauthenticatedTwoFactorAuthenticationVerifiedMethodData[] | null>(null);

  // const { initTracking } = useTracking(config.gaUaTrackingCode, config.ga4TrackingCode);

  useEffect(() => {
    // if javascript is disabled, this useEffect won't be fired, and the login form won't be rendered
    setPageLoadedWithJs(true);
    // initTracking();
  }, []);

  // handle validate
  const onValidate = (values: FormValues) => {
    if (!values.signin.username) {
      return { username: 'Username is required' };
    }
    if (username && !values.signin.password) {
      return { password: 'Password is required' };
    }
    return {};
  };

  // handle submit
  const onSubmit = (values: FormValues) => {
    if (isVirtual) {
      trySignin(values);
    } else if (!username) {
      setUsername(values.signin.username);
    } else {
      setPassword(values.signin.password);

      trySignin(values);
    }
  };

  const determineUpdatedRedirectData = (
    accountId: number,
    pending?: boolean
  ): { updatedRedirect: string; updatedNext: string } => {
    if (isVirtual) {
      return { updatedRedirect: redirect || `${Config.virtual.defaultRedirect}`, updatedNext: next || '/' };
    }

    if (!pending) {
      if (redirect && clientType !== UIClientTypes.application) {
        return { updatedRedirect: redirect, updatedNext: next || '/' };
      } else {
        return { updatedRedirect: `${Config.dashboard.defaultRedirect}/auth`, updatedNext: '/' };
      }
    } else {
      if (clientType === UIClientTypes.application && redirect) {
        return { updatedRedirect: redirect, updatedNext: next || '/' };
      } else {
        return {
          updatedRedirect: `${Config.applicationUrl}auth`,
          updatedNext: (accountId > 0 && '/status') || '/home',
        };
      }
    }
  };

  const onSuccessfulLogin = async (
    token: string,
    accessToken: string,
    accountId: number,
    pending: boolean,
    userLevel: UserProfileLevel,
    twoFactorAuthenticationState: TwoFactorAuthenticationStates | null,
    responseCode?: ResponseCode,
    isPendingMarketDataEntitlementPolicyUpdatePresent?: boolean,
  ) => {
    if (responseCode === 113 && userLevel !== UserProfileLevel.Account) {
      setAuthError('Your password has expired. Please contact support to change your password.');
      return;
    }

    let redirectRoute = '';

    const { updatedRedirect, updatedNext } = determineUpdatedRedirectData(accountId, pending);

    if (responseCode === 112 || responseCode === 113) {
      redirectRoute = `/reset-password/${accessToken}?loginResponseCode=${responseCode}&userLevel=${userLevel}`;
    } else if (responseCode === 401 || responseCode === 400) {
      // We are routing to the market data questionnaire page in the case of 400 or 401 response codes from the get token endpoint.
      // Only route to the market data questionnaire page in the case where the user does not have any pending entitlement requests.
      if (!isPendingMarketDataEntitlementPolicyUpdatePresent) {
        redirectRoute = `/market-data-questionnaire?accessToken=${accessToken}&accountId=${accountId}&token=${token}&redirect=${updatedRedirect}&next=${updatedNext}&userLevel=${userLevel}&pending=${pending}`
      }
    } else if (!pending && !isVirtual && userLevel === UserProfileLevel.Account) {
      // Send request for if the user has signed all documents
      const agreements = await (await fetch(
        `${
          Config.environment === TradingBlockEnvironments.dashboard
            ? Config.dashboard.tradingApi
            : Config.virtual.tradingApi
        }/Accounts/GetDocumentStatus?accountId=${accountId}`,
        {
          headers: {
            authorization: `${accessToken}`,
          },
        }
      )).json();

      if (Object.values(agreements.Payload).includes(false)) {
        redirectRoute = `/updated-agreements?token=${token}&accessToken=${accessToken}&accountId=${accountId}`;
      }
    }

    if (!isVirtual && twoFactorAuthenticationState && twoFactorAuthenticationState !== TwoFactorAuthenticationStates.EnabledWithSmsAndEmailMethodsVerified) {
      history.push(`/two-factor-authentication-setup?accessToken=${accessToken}&accountId=${accountId}&token=${token}${redirectRoute ? `&redirectRoute=${encodeURIComponent(redirectRoute)}` : ''}`);
      return;
    }

    if (redirectRoute) {
      history.push(redirectRoute);
      return;
    }

    const hashedParams = toHashParams({
      id_token: token,
      grp: group,
    });

    // For external redirects to other services (like dashboard), the base URL already includes
    // the branch prefix (from env vars modified by pipeline). The 'next' parameter should be
    // a path relative to that service's basename, not include the branch prefix again.
    // Since the external service (dashboard) has its own BrowserRouter with basename,
    // we should pass the path without the branch prefix in the 'next' parameter.
    // The external service will handle adding its own branch prefix via BrowserRouter basename.
    window.location.href = `${updatedRedirect}?next=${updatedNext}${hashedParams}`;
  };

  const handleTryLoginSuccessWithToken = async (token: string, responseCode?: ResponseCode) => {
    // Decode JWT.
    const publicKey = await (await fetch(
      getPath(apiRootUrl === Config.virtual.apiRoot ? `/${apiRootUrl}/publickey` : '/publickey')
    )).json();
    const {
      token: accessToken,
      accountId,
      pending,
      userLevel,
      isPendingMarketDataEntitlementPolicyUpdatePresent,
      twoFactorAuthenticationState,
    } = jwt.verify(token, publicKey.key) as LoginJwtValue;

    await onSuccessfulLogin(
      token,
      accessToken,
      accountId,
      pending,
      userLevel,
      twoFactorAuthenticationState,
      responseCode,
      isPendingMarketDataEntitlementPolicyUpdatePresent,
    );
  };

  const trySignin = async (values: FormValues) => {
    if (isAuthenticating) {
      return;
    }

    if (!values.signin.username || !values.signin.password) {
      setAuthError('Username/Password not set');
      return;
    }

    setAuthError(undefined);
    setIsAuthenticating(true);

    const loginValues = { ...values.signin };

    await TryLogin(loginValues, { subroot: apiRootUrl, subdirectory: getSubdirectory() }).then(
      async ({
        token,
        success,
        reason,
        responseCode,
        unauthenticatedTwoFactorAuthenticationVerifiedMethodsData: unauthenticatedTwoFactorAuthenticationVerifiedMethodsDataFromTryLogin,
      }) => {
        try {
          if (success) {
            if (token) {
              await handleTryLoginSuccessWithToken(token, responseCode);
            } else if (
              !token &&
              unauthenticatedTwoFactorAuthenticationVerifiedMethodsDataFromTryLogin &&
              unauthenticatedTwoFactorAuthenticationVerifiedMethodsDataFromTryLogin.length > 0
            ) {
              setShowTwoFactorAuthenticationComponent(true);
              setVerifiedTwoFactorAuthenticationMethodsData(unauthenticatedTwoFactorAuthenticationVerifiedMethodsDataFromTryLogin);
            } else {
              throw new Error(GENERIC_LOGIN_ERROR_MSG);
            }
          } else {
            throw reason;
          }
        } catch (error: any) {
          if (typeof error === 'string' && error) {
            setAuthError(error);
          } else if (typeof error === 'object' && error && error.message) {
            setAuthError(error.message);
          } else {
            setAuthError(GENERIC_LOGIN_ERROR_MSG);
          }
        }

        setIsAuthenticating(false);
      }
    );
  };

  useEffect(() => {
    if (
      (
        !password ||
        !username ||
        (
          verifiedTwoFactorAuthenticationMethodsData &&
          verifiedTwoFactorAuthenticationMethodsData.length === 0
        )
      ) &&
      showTwoFactorAuthenticationComponent
    ) {
        setAuthError(GENERIC_LOGIN_ERROR_MSG);
        setShowTwoFactorAuthenticationComponent(false);
      }
  }, [
    verifiedTwoFactorAuthenticationMethodsData,
    password,
    username,
    showTwoFactorAuthenticationComponent,
  ]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <div className={isVirtual ? 'env-virtual' : ''} style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
        <Header>
          <SubHeader>
            <SiteLogo siteGroup={group} />
            <nav className="nav-utilities">
              <ul>
                <li>
                  {isVirtual ? (
                    <ExternalLink href={Config.goToLiveUrl + siteParam}>Go to live trading account</ExternalLink>
                  ) : (
                    <TradingBlockLink siteGroup={group} origin={group} to="LegacySiteHomeUrl">
                      &uarr; Go to <SiteName siteGroup={group} /> Legacy
                    </TradingBlockLink>
                  )}
                </li>
                <li>
                  <TradingBlockLink siteGroup={group} origin={group} to="CustomerServiceUrl">
                    Contact us
                  </TradingBlockLink>
                </li>
              </ul>
            </nav>
          </SubHeader>
        </Header>
        <main className="login">
          <Section>
            <div className="content">
              <div className="login-container">
                <div className="login-primary">
                  <h1>
                    <i
                      aria-hidden="true"
                      className={`fal ${isVirtual ? 'fa-vr-cardboard' : 'fa-lock-alt'}`}
                      style={{
                        paddingRight: '10px',
                      }}
                    />
                    <span style={{ fontSize: 'clamp(24px, 4vw, 28px)', wordBreak: 'break-all' }}>
                      {isVirtual ? 'Virtual Log in' : username ? username : 'Log in'}
                    </span>
                  </h1>
                  {isVirtual && (
                    <>
                      <p>
                        Welcome to Virtual Trading. If you do not have a new Virtual account,{' '}
                        <ExternalLink href={Config.virtual.applicationUrl + siteParam}>
                          create a virtual trading account
                        </ExternalLink>
                      </p>
                      <p>NOTE: Your live account login is not compatible with Virtual Trading.</p>
                    </>
                  )}
                  {pageLoadedWithJs && (
                    <>
                      {!showTwoFactorAuthenticationComponent && (
                        <Formik
                          validate={onValidate}
                          validateOnChange={true}
                          initialValues={{ signin: { username: '', password: '' } }}
                          onSubmit={onSubmit}
                        >
                          {({ errors, touched, submitCount, resetForm, handleSubmit }) => (
                            <form onSubmit={handleSubmit}>
                              {!username && (
                                <FormGroup>
                                  <Field
                                    component={TextboxField}
                                    id="signin.username"
                                    placeholder="Username"
                                    autoFocus={true}
                                  />
                                  {errors['username'] && (touched['username'] || !!submitCount) && (
                                    <span className="error">{errors['username']}</span>
                                  )}
                                </FormGroup>
                              )}
                              {(isVirtual || username) && (
                                <FormGroup>
                                  <Field
                                    type="password"
                                    component={TextboxField}
                                    id="signin.password"
                                    placeholder="Password"
                                    autoFocus={!isVirtual}
                                    allowUnmask
                                  />
                                  {errors['password'] && (touched['password'] || !!submitCount) && (
                                    <span className="error">{errors['password']}</span>
                                  )}
                                </FormGroup>
                              )}
                              <button
                                className={`btn btn-block btn-light btn-icon ${isVirtual ? 'btn-virtual' : ''}`}
                                disabled={!!isAuthenticating}
                                type="submit"
                              >
                                {isVirtual || username ? 'Log in' : 'Next'}
                                <span className="tb-icon">
                                  {isVirtual ? <i className="fal fa-vr-cardboard" /> : <Svg path="icon-arrow" />}
                                </span>
                              </button>
                              {username && (
                                <>
                                  <p
                                    style={{
                                      marginTop: '2rem',
                                      marginBottom: '0',
                                      color: 'rgba(255, 255, 255, 0.9)',
                                    }}
                                  >
                                    <strong>Want to use a different account?</strong>{' '}
                                    <button
                                      style={{ color: '#19bab4' }}
                                      onClick={() => {
                                        setUsername(undefined);
                                        setPassword(null);
                                        resetForm(); // set the formik values to empty
                                      }}
                                    >
                                      Go back
                                    </button>
                                  </p>
                                </>
                              )}
                            </form>
                          )}
                        </Formik>
                      )}

                      {
                        showTwoFactorAuthenticationComponent &&
                        username &&
                        password &&
                        verifiedTwoFactorAuthenticationMethodsData &&
                        verifiedTwoFactorAuthenticationMethodsData.length > 0 &&
                        (
                          <TwoFactorAuthenticationWrapper
                            password={password}
                            username={username}
                            setUsername={setUsername}
                            setPassword={setPassword}
                            handleTryLoginSuccessWithToken={handleTryLoginSuccessWithToken}
                            verifiedTwoFactorAuthenticationMethodsData={verifiedTwoFactorAuthenticationMethodsData}
                            setShowTwoFactorAuthenticationComponent={setShowTwoFactorAuthenticationComponent}
                          />
                        )
                      }
                    </>
                  )}

                  {authError && !isAuthenticating && (
                    <p
                      className="error"
                      style={{ marginBottom: 0, marginTop: '1rem' }}
                    >
                      {authError}
                    </p>
                  )}

                  <noscript>
                    <div className="alert alert-danger">
                      <h4 className="error" style={{ marginBottom: 0 }}>
                        Javascript is required for this site.
                      </h4>
                    </div>
                  </noscript>

                  <LoginCardAuthFooter
                    isVirtual={isVirtual}
                    group={group}
                    siteParam={siteParam}
                    environmentConfig={config}
                    recovery={
                      showTwoFactorAuthenticationComponent ? (
                        <TradingBlockLink
                          className="login-primary-recovery-link"
                          siteGroup={group}
                          origin={group}
                          to="CustomerServiceUrl"
                        >
                          Contact us
                        </TradingBlockLink>
                      ) : (
                        <Link className="login-primary-recovery-link" to={`/forgot-password?grp=${grp}`}>
                          Forgot password
                        </Link>
                      )
                    }
                    showSecondaryNav={!showTwoFactorAuthenticationComponent}
                  />
                </div>
                <div className="login-secondary">
                  <img alt="" aria-hidden="true" src={getStaticPath('/images/sign-in-hero.png')} />
                </div>
              </div>
            </div>
          </Section>
        </main>
        <footer className="footer">
          <div className="content">
            <FooterTeaser siteGroup={group} />
          </div>
        </footer>
      </div>
    </div>
  );
};
